const MARKET_SEPARATOR = '-';

const createDisplayNames = (locale) => {
  if (!Intl?.DisplayNames) {
    return null;
  }

  return {
    regionName: new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }),
    languageName: new Intl.DisplayNames([locale], { type: 'language', fallback: 'none' }),
  };
};

const memoizedDisplayNames = new Map();

const getDisplayNames = (locale) => {
  // we only want to create one pair of DisplayNames per locale
  // and keep reusing it to improve performance
  if (memoizedDisplayNames.has(locale)) {
    return memoizedDisplayNames.get(locale);
  }

  const displayNames = createDisplayNames(locale);
  if (displayNames) {
    memoizedDisplayNames.set(locale, displayNames);
    return displayNames;
  }

  return null;
};

/**
 * Returns a localised name for a given language.
 *
 * @param {string} locale Unicode BCP 47 locale code
 * @param {string} languageCode language code in ISO 639-1 format
 * @returns {string|null} localised string if successful, null otherwise
 */
const localiseLanguage = (locale, languageCode) => {
  if (!languageCode) {
    return null;
  }
  const displayNames = getDisplayNames(locale);
  return displayNames?.languageName.of(languageCode) ?? null;
};

exports.localiseLanguage = localiseLanguage;

/**
 * Returns a localised name for a given market.
 *
 * @param {string} locale Unicode BCP 47 locale code
 * @param {string} marketCode market code (also in BCP 47 format)
 * @param {boolean} includeLanguage whether the localised string should include the language part
 *   (useful for markets with multiple language options)
 * @returns {string|null} localised string if successful, null otherwise
 */
exports.localiseMarket = (locale, marketCode, includeLanguage = false) => {
  const displayNames = getDisplayNames(locale);
  if (!displayNames) {
    return null;
  }

  const [languageCode, regionCode] = marketCode.split(MARKET_SEPARATOR);
  // Node.js requires the regional code to be upper case, otherwise it does not match
  const regionName = displayNames.regionName.of(regionCode.toUpperCase());
  if (includeLanguage) {
    const languageName = localiseLanguage(locale, languageCode);
    // Chrome on Android 9 doesn't have a working 'language' DisplayNames
    // so in this case it's better to return null and let the application fall back
    if (!languageName) {
      return null;
    }
    return `${regionName} (${languageName})`;
  }

  return regionName;
};
